import React, { useState } from "react";
import { Button, Empty, Segmented, Tag, Tooltip, Typography } from "antd";
import { CarOutlined, EnvironmentOutlined, PlusOutlined, ToolOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { isRtlLanguage } from "../../../i18n.jsx";
import ActionSheet from "../../../design-system/ActionSheet";
import { berlinParts, clockToMinutes, dayOf } from "./calc";
import { ConflictBadge } from "./Conflicts";
import { absenceLabels, accuracyHint, dayLabel, kindLabels, timeRange } from "./labels";

const UNASSIGNED = "";
const DRAG_TYPE = "application/x-dispatch";

/** Drag payload of a card or a queue entry. */
export const dragData = event => {
  try {
    return JSON.parse(event.dataTransfer.getData(DRAG_TYPE) || "null");
  } catch {
    return null;
  }
};

export const startDrag = (event, payload) => {
  event.dataTransfer.setData(DRAG_TYPE, JSON.stringify(payload));
  event.dataTransfer.effectAllowed = "move";
};

const vehicleName = (vehicles, id) => {
  const v = (vehicles || []).find(x => x.id === id);
  return v ? v.plate || v.name : "";
};

/** One planned item: time, title, customer, vehicle, warnings. */
export const ItemCard = ({ item, board, from, draggable, onOpen, compact = false }) => {
  const { t } = useTranslation("dashboard");
  const kinds = kindLabels(t);
  return (
    <div
      className={`dsp-card dsp-card--${item.kind}`}
      draggable={draggable}
      onDragStart={event => startDrag(event, { type: "item", id: item.id, from })}
      onClick={() => onOpen(item)}
      onKeyDown={event => event.key === "Enter" && onOpen(item)}
      role="button"
      tabIndex={0}
      title={kinds[item.kind]}
    >
      <div className="dsp-card__top">
        <span className="dsp-card__time">{timeRange(item)}</span>
        <ConflictBadge conflicts={item.conflicts} t={t} />
      </div>
      <div className="dsp-card__title">{item.title}</div>
      {!compact && item.client && <div className="dsp-card__meta">{item.client.name}</div>}
      {!compact && item.place?.zip && (
        <div className="dsp-card__meta">
          <EnvironmentOutlined /> {item.address}
        </div>
      )}
      {(item.vehicle || item.tools?.length > 0) && (
        <div className="dsp-card__meta">
          {item.vehicle && (
            <span>
              <CarOutlined /> {vehicleName(board.vehicles, item.vehicle)}
            </span>
          )}
          {item.tools?.length > 0 && (
            <span>
              {" "}
              <ToolOutlined /> {item.tools.length}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

/** Drive before an item (from the Betriebshof or the previous job). */
const TravelHint = ({ leg }) => {
  const { t } = useTranslation("dashboard");
  if (!leg) return null;
  return (
    <Tooltip title={accuracyHint(leg, t)}>
      <div className={`dsp-travel${leg.tight ? " dsp-travel--tight" : ""}`}>
        <CarOutlined /> {t("{{minutes}} min · {{km}} km", leg)}
        {leg.tight && ` · ${t("tight")}`}
      </div>
    </Tooltip>
  );
};

const Absences = ({ absences }) => {
  const { t } = useTranslation("dashboard");
  const labels = absenceLabels(t);
  return (absences || []).map((a, i) => (
    <Tag key={i} color={a.kind === "school" ? "purple" : "orange"} className="dsp-absence">
      {labels[a.kind] || a.label}
    </Tag>
  ));
};

const legsByTarget = (board, employee, day) =>
  new Map((board.travel[`${employee}|${day}`] || []).map(l => [l.to, l]));

/**
 * Week view (desktop): a row per employee, a column per day; cards are dragged to another day
 * or person, queue entries dropped onto a cell. The first row holds items without anyone.
 */
export const WeekGrid = ({ board, canMove, onOpen, onDrop, onAdd }) => {
  const { t, i18n } = useTranslation("dashboard");
  const [over, setOver] = useState(null);
  const itemOf = new Map(board.items.map(i => [i.id, i]));
  const rows = [
    { employee: UNASSIGNED, name: t("Not assigned"), days: null },
    ...board.rows.map(r => ({ ...r, name: board.staff.find(s => s.id === r.employee)?.name }))
  ];
  const today = dayOf(new Date());
  return (
    <div className="dsp-week" style={{ "--dsp-days": board.days.length }}>
      <div className="dsp-week__corner" />
      {board.days.map(d => (
        <div
          key={d.day}
          className={`dsp-week__head${d.day === today ? " dsp-today" : ""}${d.holiday ? " dsp-holiday" : ""}`}
        >
          <div>{dayLabel(d.day, i18n.language)}</div>
          {d.holiday && <div className="dsp-holiday__name">{d.holiday}</div>}
        </div>
      ))}
      {rows.map(row => (
        <React.Fragment key={row.employee || "unassigned"}>
          <div className={`dsp-week__name${row.employee ? "" : " dsp-week__name--open"}`}>
            {row.name}
          </div>
          {board.days.map(d => {
            const ids = row.employee ? row.days[d.day].items : board.unassigned[d.day] || [];
            const legs = row.employee ? legsByTarget(board, row.employee, d.day) : new Map();
            const key = `${row.employee}|${d.day}`;
            return (
              <div
                key={key}
                className={`dsp-cell${over === key ? " dsp-cell--over" : ""}${d.holiday ? " dsp-holiday" : ""}`}
                onDragOver={event => {
                  if (!canMove) return;
                  event.preventDefault();
                  setOver(key);
                }}
                onDragLeave={() => setOver(current => (current === key ? null : current))}
                onDrop={event => {
                  event.preventDefault();
                  setOver(null);
                  onDrop(dragData(event), { day: d.day, employee: row.employee || null });
                }}
              >
                {row.employee && <Absences absences={row.days[d.day].absences} />}
                {ids.map(id => {
                  const item = itemOf.get(id);
                  return (
                    <React.Fragment key={id}>
                      <TravelHint leg={legs.get(id)} />
                      <ItemCard
                        item={item}
                        board={board}
                        from={row.employee || null}
                        draggable={canMove}
                        onOpen={onOpen}
                      />
                    </React.Fragment>
                  );
                })}
                {onAdd && row.employee && (
                  <Button
                    type="text"
                    size="small"
                    className="dsp-cell__add"
                    icon={<PlusOutlined />}
                    aria-label={t("Plan job")}
                    onClick={() => onAdd({ day: d.day, employees: [row.employee] })}
                  />
                )}
              </div>
            );
          })}
        </React.Fragment>
      ))}
    </div>
  );
};

/**
 * Day view (desktop): a timeline per employee from the start to the end of the working day
 * (plus margins); dropping a card sets person and start time (15-minute steps).
 */
export const DayTimeline = ({ board, day, canMove, onOpen, onDrop }) => {
  const { t, i18n } = useTranslation("dashboard");
  const rtl = isRtlLanguage(i18n.language);
  const first = Math.max(0, Math.floor((clockToMinutes(board.settings.dayStart) ?? 420) / 60) - 1);
  const last = Math.min(24, Math.ceil((clockToMinutes(board.settings.dayEnd) ?? 960) / 60) + 3);
  const span = (last - first) * 60;
  const hours = Array.from({ length: last - first }, (_, i) => first + i);
  const itemOf = new Map(board.items.map(i => [i.id, i]));
  const position = item => {
    const from = Math.max(dayOf(item.start) === day ? minutesOf(item.start) : 0, first * 60);
    const to = Math.min(dayOf(item.end) === day ? minutesOf(item.end) : 1440, last * 60);
    return {
      insetInlineStart: `${((from - first * 60) / span) * 100}%`,
      width: `${Math.max(((to - from) / span) * 100, 3)}%`
    };
  };
  const minutesAt = (event, lane) => {
    const rect = lane.getBoundingClientRect();
    const share = rtl
      ? (rect.right - event.clientX) / rect.width
      : (event.clientX - rect.left) / rect.width;
    return Math.round((first * 60 + share * span) / 15) * 15;
  };
  const rows = [
    { employee: UNASSIGNED, name: t("Not assigned") },
    ...board.rows.map(r => ({ ...r, name: board.staff.find(s => s.id === r.employee)?.name }))
  ];
  return (
    <div className="dsp-day">
      <div className="dsp-day__name" />
      <div className="dsp-day__hours">
        {hours.map(h => (
          <span key={h} style={{ insetInlineStart: `${(((h - first) * 60) / span) * 100}%` }}>
            {String(h).padStart(2, "0")}
          </span>
        ))}
      </div>
      {rows.map(row => {
        const ids = row.employee ? row.days[day]?.items || [] : board.unassigned[day] || [];
        return (
          <React.Fragment key={row.employee || "unassigned"}>
            <div className={`dsp-day__name${row.employee ? "" : " dsp-week__name--open"}`}>
              {row.name}
              {row.employee && <Absences absences={row.days[day]?.absences} />}
            </div>
            <div
              className="dsp-day__lane"
              onDragOver={event => canMove && event.preventDefault()}
              onDrop={event => {
                event.preventDefault();
                onDrop(dragData(event), {
                  day,
                  employee: row.employee || null,
                  startMinutes: minutesAt(event, event.currentTarget)
                });
              }}
            >
              {hours.map(h => (
                <span
                  key={h}
                  className="dsp-day__tick"
                  style={{ insetInlineStart: `${(((h - first) * 60) / span) * 100}%` }}
                />
              ))}
              {ids.map(id => {
                const item = itemOf.get(id);
                return (
                  <div key={id} className="dsp-day__block" style={position(item)}>
                    <ItemCard
                      item={item}
                      board={board}
                      from={row.employee || null}
                      draggable={canMove}
                      onOpen={onOpen}
                      compact
                    />
                  </div>
                );
              })}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

const minutesOf = value => berlinParts(value).minutes;

/**
 * Phones: one day as a list per employee. Tapping a job opens an action sheet (open, move to
 * another day / time / person, navigate); nothing depends on dragging.
 */
export const MobileDay = ({ board, day, onDay, canMove, onOpen, onMove }) => {
  const { t, i18n } = useTranslation("dashboard");
  const [sheet, setSheet] = useState(null);
  const itemOf = new Map(board.items.map(i => [i.id, i]));
  const unassigned = board.unassigned[day] || [];
  const rows = board.rows.filter(r => r.days[day]);
  const today = dayOf(new Date());
  return (
    <div className="dsp-mobile">
      <Segmented
        block
        className="dsp-mobile__days"
        value={day}
        onChange={onDay}
        options={board.days.map(d => ({
          value: d.day,
          label: (
            <span className={d.day === today ? "dsp-today-text" : ""}>
              {dayLabel(d.day, i18n.language).split(/[,\s]+/)[0]}
            </span>
          )
        }))}
      />
      <Typography.Title level={5} className="dsp-mobile__date">
        {dayLabel(day, i18n.language, { long: true })}
        {board.days.find(d => d.day === day)?.holiday && (
          <Tag color="gold" className="dsp-absence">
            {board.days.find(d => d.day === day).holiday}
          </Tag>
        )}
      </Typography.Title>
      {unassigned.length > 0 && (
        <section className="dsp-mobile__group">
          <div className="dsp-mobile__name dsp-week__name--open">{t("Not assigned")}</div>
          {unassigned.map(id => (
            <ItemCard
              key={id}
              item={itemOf.get(id)}
              board={board}
              from={null}
              onOpen={setSheetFor(setSheet, null)}
            />
          ))}
        </section>
      )}
      {rows.map(row => {
        const cell = row.days[day];
        const legs = legsByTarget(board, row.employee, day);
        const name = board.staff.find(s => s.id === row.employee)?.name;
        return (
          <section key={row.employee} className="dsp-mobile__group">
            <div className="dsp-mobile__name">
              {name} <Absences absences={cell.absences} />
            </div>
            {cell.items.length === 0 && (
              <Typography.Text type="secondary" className="dsp-small">
                {t("Nothing planned")}
              </Typography.Text>
            )}
            {cell.items.map(id => (
              <React.Fragment key={id}>
                <TravelHint leg={legs.get(id)} />
                <ItemCard
                  item={itemOf.get(id)}
                  board={board}
                  from={row.employee}
                  onOpen={setSheetFor(setSheet, row.employee)}
                />
              </React.Fragment>
            ))}
          </section>
        );
      })}
      {rows.length === 0 && unassigned.length === 0 && <Empty description={t("Nothing planned")} />}
      <ActionSheet open={Boolean(sheet)} onClose={() => setSheet(null)} title={sheet?.item.title}>
        <Button onClick={() => onOpen(sheet.item)}>{t("Open")}</Button>
        {canMove && <Button onClick={() => onMove(sheet.item, sheet.from)}>{t("Move to…")}</Button>}
        {sheet?.item.address && (
          <Button
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(sheet.item.address)}`}
            target="_blank"
            rel="noreferrer"
          >
            {t("Open in maps")}
          </Button>
        )}
      </ActionSheet>
    </div>
  );
};

const setSheetFor = (setSheet, from) => item => setSheet({ item, from });
